import { useQuery, useInfiniteQuery } from 'react-query';
import HttpService from 'services/httpService';
import { ENDPOINT_BASE_URL } from './constants';
import { useAuth } from './useAuth';
import { Patient, PaginatedPatientsResponse, PatientListParams } from 'models/PatientModels';
import { useMemo } from 'react';



export const paginatedPatientQueryKeys = {
    paginatedPatientsKey: (orgId: string) => ['paginatedPatients', { orgId }],
    getSinglePatientKey: (patientId: string | undefined) => ['singlePatient', { activePatientId: patientId }],
};

export type PaginatedPatientsQueryCache = {
    data: PaginatedPatientsResponse;
    ts: number;
} | undefined;

export type PatientQueryCache = {
    data: {
        patient: Patient;
    };
    ts: number;
} | undefined;

interface UsePaginatedPatientDataOptions {
    preventRefetch?: boolean;
    patientId?: string;
    limit?: number;
    enabled?: boolean;
    includeTotalCount?: boolean;
    includeLatestEvaluation?: boolean;
    includeArchived?: boolean;
    searchQuery?: string; // Add search support
}

export const usePaginatedPatientData = (options?: UsePaginatedPatientDataOptions) => {
    const { accessToken, user } = useAuth();

    const getPaginatedPatientsByOrg = async ({ pageParam }: { pageParam?: string }) => {
        if (!user || !accessToken) return;

        const params: PatientListParams = {
            org_id: user.orgs[0].org_id,
            limit: options?.limit || 15,
            include_total_count: options?.includeTotalCount || false,
            include_latest_evaluation: options?.includeLatestEvaluation || false,
            include_archived: options?.includeArchived || false,
        };

        if (pageParam) {
            params.next_token = pageParam;
        }

        const queryParams = new URLSearchParams();
        queryParams.append('org_id', params.org_id);
        if (params.limit) {
            queryParams.append('limit', params.limit.toString());
        }
        if (params.next_token) {
            queryParams.append('next_token', params.next_token);
        }
        if (params.include_total_count) {
            queryParams.append('include_total_count', params.include_total_count.toString());
        }
        if (params.include_latest_evaluation) {
            queryParams.append('include_latest_evaluation', params.include_latest_evaluation.toString());
        }
        if (params.include_archived) {
            queryParams.append('include_archived', params.include_archived.toString());
        }
        if (options?.searchQuery) {
            queryParams.append('q', options.searchQuery);
        }

        const data = await HttpService.get<PaginatedPatientsResponse>({
            url: `${ENDPOINT_BASE_URL}/patients/?${queryParams.toString()}`,
            token: accessToken,
        });
        return data;
    };

    const getSinglePatientByOrg = async ({ queryKey }: any) => {
        // eslint-disable-next-line
        const [_key, { activePatientId }] = queryKey;

        if (!user || !accessToken || !activePatientId) return;
        const data = await HttpService.get<{ patient: Patient }>({
            url: `${ENDPOINT_BASE_URL}/patients/${activePatientId}?org_id=${user.orgs[0].org_id}`,
            token: accessToken,
        });
        return data;
    };

    const {
        data: paginatedPatientsData,
        error: paginatedPatientsError,
        isFetching: paginatedPatientsLoading,
        isLoading: paginatedPatientsInitialLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        refetch: refetchPaginatedPatients,
    } = useInfiniteQuery(
        [
            ...paginatedPatientQueryKeys.paginatedPatientsKey(user?.orgs[0]?.org_id || ''),
            options?.searchQuery || 'no-search'
        ],
        getPaginatedPatientsByOrg,
        {
            refetchOnMount: !options?.preventRefetch,
            enabled: options?.enabled !== false && !!user && !!accessToken,
            getNextPageParam: (lastPage) => lastPage?.data?.next_token || undefined,
        },
    );

    const {
        data: singlePatientData,
        error: singlePatientError,
        isFetching: singlePatientLoading,
        refetch: refetchSinglePatient,
    } = useQuery(
        paginatedPatientQueryKeys.getSinglePatientKey(options?.patientId),
        getSinglePatientByOrg,
        {
            refetchOnMount: !options?.preventRefetch,
            enabled: !!options?.patientId,
        },
    );

    // Flatten all pages into a single array of patients
    const allPatients = useMemo(() => {
        if (!paginatedPatientsData?.pages) return [];
        const flattened = paginatedPatientsData.pages.flatMap(page => page?.data?.patients || []);
        return flattened;
    }, [paginatedPatientsData]);

    // Calculate total count from all pages if includeTotalCount is enabled
    const totalCount = useMemo(() => {
        if (!options?.includeTotalCount || !paginatedPatientsData?.pages) return null;
        // If the first page has total_count, use that (it should be the same for all pages)
        const firstPage = paginatedPatientsData.pages[0];
        const count = firstPage?.data?.total_count || null;
        return count;
    }, [paginatedPatientsData, options?.includeTotalCount]);

    // Calculate total returned from all pages
    const totalReturned = useMemo(() => {
        if (!paginatedPatientsData?.pages) return 0;
        const returned = paginatedPatientsData.pages.reduce((total, page) => {
            return total + (page?.data?.total_returned || 0);
        }, 0);
        return returned;
    }, [paginatedPatientsData]);

    // an object where each entry is comprised of a key equal to a patient's ID, and
    // the value is the corresponding patient object. used to merge patient, appointment,
    // xray, and PRO information together on various tables in the app, as well as in the
    // useActivePatient hook. This value is memoized to hopefully maximize performance
    // around it, but there is a chance this method becomes untenable in the event that an
    // org manages thousands upon thousands of patients.
    const patientsMappedToIds = useMemo<{ [key: string]: Patient } | undefined>(() => allPatients?.reduce((obj, patient) => {
        obj[patient.patient_id] = patient;
        return obj;
    }, {} as { [key: string]: Patient }), [allPatients]) || {};

    const singlePatient = singlePatientData?.data.patient;

    return {
        allPatients,
        allPatientsError: paginatedPatientsError,
        allPatientsLoading: paginatedPatientsLoading,
        allPatientsInitialLoading: paginatedPatientsInitialLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        refetchAllPatients: refetchPaginatedPatients,
        patientsMappedToIds,
        singlePatient,
        singlePatientError,
        singlePatientLoading,
        refetchSinglePatient,
        totalCount,
        totalReturned,
    };
}; 